import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Video, VideoOff, Camera } from 'lucide-react';

const MobileBroadcaster = () => {
  const { id } = useParams();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState(null);
  
  const localVideoRef = useRef(null);
  const streamRef = useRef(null);
  const socketRef = useRef(null);
  const peerConnectionsRef = useRef({});

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:11200';

  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, []);

  useEffect(() => {
    if (isStreaming && localVideoRef.current && streamRef.current) {
      localVideoRef.current.srcObject = streamRef.current;
    }
  }, [isStreaming]);

  const stopStreaming = () => {
    setIsStreaming(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.emit('stream-stopped', id);
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
  };

  const startStreaming = async () => {
    try {
      setStreamError(null);
      // Solicitar cámara trasera por defecto
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: { ideal: 'environment' } }, 
        audio: false 
      });
      streamRef.current = stream;
      setIsStreaming(true);

      const socket = io(apiUrl);
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join-event', id);
        socket.emit('stream-started', id);
      });

      socket.on('viewer-joined', async (viewerSocketId) => {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        peerConnectionsRef.current[viewerSocketId] = pc;

        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            socket.emit('ice-candidate', { eventId: id, target: viewerSocketId, candidate: event.candidate });
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('offer', { eventId: id, target: viewerSocketId, offer });
      });

      socket.on('answer', async (data) => {
        const pc = peerConnectionsRef.current[data.sender];
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
      });

      socket.on('ice-candidate', async (data) => {
        const pc = peerConnectionsRef.current[data.sender];
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      });

    } catch (err) {
      console.error('Error accessing camera:', err);
      setStreamError('No se pudo acceder a la cámara. Revisa los permisos.');
    }
  };

  return (
    <div className="fixed inset-0 bg-[#080808] flex flex-col overflow-hidden text-white">
      {/* Header */}
      <div className="absolute top-0 inset-x-0 p-6 z-20 flex justify-center bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-3">
          <Camera className="text-purple-400" />
          <h1 className="text-xl font-black uppercase tracking-widest text-white/90">Operador Móvil</h1>
        </div>
      </div>

      {/* Video Area */}
      <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
        {isStreaming ? (
          <>
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            <div className="absolute top-24 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-1.5 bg-red-500/80 backdrop-blur-md rounded-full shadow-[0_0_20px_rgba(239,68,68,0.5)] animate-pulse">
              <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
              <span className="text-xs font-black uppercase tracking-[3px] text-white">EN VIVO</span>
            </div>
          </>
        ) : (
          <div className="text-center p-8 text-white/30">
            <Camera size={64} className="mx-auto mb-6 opacity-20" />
            <p className="font-bold uppercase tracking-widest text-sm mb-2">Cámara Lista</p>
            <p className="text-xs font-medium">Presiona el botón para iniciar la transmisión a la pantalla gigante.</p>
            {streamError && (
              <div className="mt-6 bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-2xl text-xs font-medium">
                {streamError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls Area */}
      <div className="absolute bottom-0 inset-x-0 p-8 pb-12 z-20 flex justify-center bg-gradient-to-t from-black via-black/80 to-transparent">
        {isStreaming ? (
          <button
            onClick={stopStreaming}
            className="group w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center border-2 border-red-500 transition-all active:scale-95 shadow-[0_0_30px_rgba(239,68,68,0.3)]"
          >
            <div className="w-12 h-12 bg-red-500 rounded-[12px] shadow-lg flex items-center justify-center text-white transition-transform group-hover:scale-90">
              <VideoOff size={24} />
            </div>
          </button>
        ) : (
          <button
            onClick={startStreaming}
            className="group w-24 h-24 rounded-full bg-white/5 flex items-center justify-center border-4 border-white/20 transition-all hover:border-white/40 active:scale-95"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-black shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-transform group-hover:scale-110">
              <Video size={24} className="ml-1" />
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileBroadcaster;
